import React, { useCallback } from "react";
import { connect } from "react-redux";

import Form from "antd/es/form";
import { FormInstance } from "antd/es/form/hooks/useForm";
import Radio from "antd/es/radio";
import Space from "antd/es/space";
import { useIntl } from "react-intl";

import Organization from "@mapmycustomers/shared/types/Organization";
import { isDefined } from "@mapmycustomers/shared/util/assert";

import RadioGroup from "@app/component/input/RadioGroup";
import DealClosedDateSettingValue from "@app/enum/DealClosedDateSettingValue";
import OrganizationSetting from "@app/enum/OrganizationSetting";
import DealsSettings from "@app/scene/settings/type/DealsSettings";
import showSaveSettingSuccessfulMessage from "@app/scene/settings/util/showSaveSettingSuccessfulMessage";
import { getOrganizationId, getOrganizationSettings } from "@app/store/iam";
import { updateOrganization } from "@app/store/iam/actions";
import { RootState } from "@app/store/rootReducer";
import useSetting from "@app/util/hook/useSetting";

import styles from "./Deals.module.scss";

interface Props {
  form: FormInstance<DealsSettings>;
  onUpdateOrganization: typeof updateOrganization.request;
  organizationId?: number;
  organizationSettings: Organization["settings"];
  setDirty: () => void;
  unsetDirty: () => void;
}

const Deals: React.FC<Props> = ({
  form,
  onUpdateOrganization,
  organizationId,
  organizationSettings,
  setDirty,
  unsetDirty,
}) => {
  const intl = useIntl();
  const setting = useSetting(
    organizationSettings,
    OrganizationSetting.CLOSED_DATE_UPDATE_PREFERENCE
  );

  const handleSubmit = useCallback(() => {
    const { closedDateUpdatePreference } = form.getFieldsValue();
    if (organizationId) {
      onUpdateOrganization({
        callback: () => {
          showSaveSettingSuccessfulMessage(intl);
          unsetDirty();
        },
        organization: { id: organizationId },
        settings: [
          setting && closedDateUpdatePreference
            ? { ...setting, value: closedDateUpdatePreference }
            : undefined,
        ].filter(isDefined),
      });
    }
  }, [setting, form, intl, organizationId, onUpdateOrganization, unsetDirty]);

  return (
    <Form
      form={form}
      initialValues={{
        closedDateUpdatePreference: setting?.value,
      }}
      onFieldsChange={setDirty}
      onFinish={handleSubmit}
    >
      <div className={styles.title}>
        {intl.formatMessage({
          id: "settings.organizationDetails.deals.description",
          defaultMessage:
            "When a user in your organization closes a deal, the deal close date should:",
          description: "Settings organization details - Deals - description",
        })}
      </div>
      <Form.Item name={OrganizationSetting.CLOSED_DATE_UPDATE_PREFERENCE}>
        <RadioGroup size="small">
          <Space className={styles.space} direction="vertical">
            <Radio value={DealClosedDateSettingValue.CHANGE_TO_TODAY}>
              {intl.formatMessage({
                id: "settings.organizationDetails.deals.changeToToday",
                defaultMessage: "Change to today’s date",
                description: "Settings organization details - Deals - Change to today",
              })}
            </Radio>
            <Radio value={DealClosedDateSettingValue.REMAIN_SAME}>
              {intl.formatMessage({
                id: "settings.organizationDetails.deals.keepSame",
                defaultMessage: "Remain the same",
                description: "Settings organization details - Deals - Remain the same",
              })}
            </Radio>
            <Radio value={DealClosedDateSettingValue.ASK_EVERYTIME}>
              {intl.formatMessage({
                id: "settings.organizationDetails.deals.askEverytime",
                defaultMessage:
                  "Ask user each time if they would like to replace with today’s date",
                description: "Settings organization details - Deals - Ask everytime",
              })}
            </Radio>
          </Space>
        </RadioGroup>
      </Form.Item>
      <div className={styles.note}>
        {intl.formatMessage(
          {
            id: "settings.organizationDetails.deals.note",
            defaultMessage:
              "<b>Note:</b> If no close date exists, today’s date will always be applied on close.",
            description: "Deal closing date setting note",
          },
          { b: (text) => <b>{text}</b> }
        )}
      </div>
    </Form>
  );
};

const mapStateToProps = (state: RootState) => ({
  organizationId: getOrganizationId(state),
  organizationSettings: getOrganizationSettings(state) ?? [],
});

const mapDispatchToProps = {
  onUpdateOrganization: updateOrganization.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(Deals);
